import React, { useEffect, useState } from 'react';
import slideshow421 from '../../assets/images/slideshow-4.2-1.jpg';
import slideshow422 from '../../assets/images/slideshow-4.2-2.jpg';
import slideshow423 from '../../assets/images/slideshow-4.2-3.jpg';
import slideshow424 from '../../assets/images/slideshow-4.2-4.jpg';
import slideshow425 from '../../assets/images/slideshow-4.2-5.jpg';
import slideshow426 from '../../assets/images/slideshow-4.2-6.jpg';
import slideshow427 from '../../assets/images/slideshow-4.2-7.jpg';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Tooltip } from '@mui/material';

const Slideshow = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slideshowImages = [
    { src: slideshow422, alt: 'Completed Braille Skills Puzzle with dark navy tray and white pieces showing raised geometric shapes and Braille numerals', tooltip: 'Completed Braille Skills Puzzle with dark navy tray and white pieces showing raised geometric shapes and Braille numerals' },
    { src: slideshow423, alt: 'Braille Skills Puzzle with white pieces partially placed in dark navy tray', tooltip: 'Braille Skills Puzzle with white pieces partially placed in dark navy tray' },
    { src: slideshow424, alt: 'Close-up demonstrating Braille to Braille matching with purple puzzle piece aligned to tray', tooltip: 'Close-up demonstrating Braille to Braille matching with purple puzzle piece aligned to tray' },
    { src: slideshow421, alt: 'Completed Braille Skills Puzzle with red tray and white pieces showing raised geometric shapes', tooltip: 'Completed Braille Skills Puzzle with red tray and white pieces showing raised geometric shapes' },
    { src: slideshow425, alt: 'White puzzle pieces being placed into purple tray showing geometric shapes and Braille dots', tooltip: 'White puzzle pieces being placed into purple tray showing geometric shapes and Braille dots' },
    { src: slideshow426, alt: 'Grey and white puzzle pieces scattered across purple tray during assembly', tooltip: 'Grey and white puzzle pieces scattered across purple tray during assembly' },
    { src: slideshow427, alt: 'Completed Braille Skills Puzzle with white pieces in purple tray alongside copper-finish tray', tooltip: 'Completed Braille Skills Puzzle with white pieces in purple tray alongside copper-finish tray' },
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slideshowImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slideshowImages.length) % slideshowImages.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Auto-advance slideshow
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideshowImages.length);
    }, 5000); // Change slide every 5 seconds
    return () => clearInterval(interval);
  }, [slideshowImages.length]);

  return (
    <section className="py-16 px-6" style={{ backgroundColor: 'var(--background-color)' }}>
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: 'var(--home-text-primary)' }}>
            See Our <span style={{ color: 'var(--home-text-primary)' }}>Puzzles</span> in Action
          </h2>
          <p className="text-lg max-w-3xl mx-auto leading-relaxed" style={{ color: 'var(--home-text-secondary)' }}>
            Explore our innovative Braille learning puzzles designed to enhance tactile literacy and make learning engaging.
          </p>
        </motion.div>

        {/* Slideshow */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="relative w-full max-w-6xl mx-auto"
        >
          {/* Slideshow container with fixed aspect ratio */}
          <div 
            className="relative rounded-2xl shadow-2xl border-2 overflow-hidden"
            style={{ 
              backgroundColor: 'var(--home-card-bg)', 
              borderColor: 'var(--home-card-border)',
              aspectRatio: '16/9',
              width: '100%'
            }}
          >
            {/* Current image with consistent scaling */}
            <Tooltip 
              title={slideshowImages[currentSlide].tooltip} 
              arrow 
              placement="top"
            >
              <img
                src={slideshowImages[currentSlide].src}
                alt={slideshowImages[currentSlide].alt}
                className="w-full h-full object-contain cursor-pointer"
                style={{ 
                  transition: 'opacity 0.5s ease',
                  backgroundColor: 'var(--home-card-bg)'
                }}
              />
            </Tooltip>

            {/* Left arrow */}
            <button
              onClick={prevSlide}
              className="absolute p-3 rounded-full shadow-lg hover:scale-110 transition-transform"
              style={{ 
                backgroundColor: 'var(--modal-background)', 
                color: 'var(--home-text-primary)',
                border: '2px solid var(--home-card-border)',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 20
              }}
            >
              <ChevronLeft size={24} />
            </button>

            {/* Right arrow */}
            <button
              onClick={nextSlide}
              className="absolute p-3 rounded-full shadow-lg hover:scale-110 transition-transform"
              style={{ 
                backgroundColor: 'var(--modal-background)', 
                color: 'var(--home-text-primary)',
                border: '2px solid var(--home-card-border)',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 20
              }}
            >
              <ChevronRight size={24} />
            </button>

            {/* Dots indicator */}
            <div 
              className="absolute flex space-x-2"
              style={{
                bottom: '16px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 20
              }}
            >
              {slideshowImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentSlide ? 'scale-125' : 'hover:scale-110'
                  }`}
                  style={{
                    backgroundColor: index === currentSlide ? 'var(--home-button-primary)' : 'var(--modal-background)',
                    border: '2px solid var(--home-button-primary)'
                  }}
                />
              ))}
            </div>
          </div>

          {/* Caption */}
          <p 
            className="text-center mt-4 text-sm"
            style={{ color: 'var(--home-text-secondary)' }}
          >
            {slideshowImages[currentSlide].alt} ({currentSlide + 1} of {slideshowImages.length})
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Slideshow;